import React from "react";
import { useI18n } from "../i18n";
import { matchingPreset, togglePermission } from "../utils/permissions";

/**
 * What one member may do: owner (everything), or staff with a list of
 * permissions — a screen of the dashboard and what they may do on it, plus a
 * few powers that cut across screens or happen at the till.
 *
 * Presets are a starting point, not a mode: picking one fills the boxes, and
 * any box can then be changed. The list of areas comes from the server
 * (`catalog`); this only draws it.
 *
 * value: { role, permissions }  onChange: (next value) => void
 */
const PermissionEditor = ({ catalog, value, onChange }) => {
  const { t } = useI18n();
  const isOwner = value.role === "owner";
  const granted = new Set(value.permissions);
  const preset = matchingPreset(catalog, value.permissions);

  const toggle = (permission) =>
    onChange({
      ...value,
      permissions: togglePermission(catalog, value.permissions, permission),
    });

  const chip = (active) =>
    `px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
      active
        ? "bg-primary-600 border-primary-600 text-white"
        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
    }`;

  return (
    <div className="space-y-5">
      <fieldset>
        <legend className="text-sm font-semibold text-gray-800 mb-2">
          {t("staff.permissions.role")}
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {["staff", "owner"].map((role) => (
            <label
              key={role}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                value.role === role
                  ? "border-primary-500 bg-primary-50"
                  : "border-gray-200"
              }`}
            >
              <input
                type="radio"
                name="member-role"
                checked={value.role === role}
                onChange={() => onChange({ ...value, role })}
                className="mt-1"
              />
              <span>
                <span className="block font-medium text-gray-900">
                  {t(`staff.permissions.roleName.${role}`)}
                </span>
                <span className="block text-xs text-gray-500">
                  {t(`staff.permissions.roleHint.${role}`)}
                </span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {!isOwner && (
        <>
          <div>
            <p className="text-sm font-semibold text-gray-800 mb-2">
              {t("staff.permissions.startFrom")}
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(catalog.presets).map(([name, list]) => (
                <button
                  key={name}
                  type="button"
                  aria-pressed={preset === name}
                  onClick={() => onChange({ ...value, permissions: list })}
                  className={chip(preset === name)}
                >
                  {t(`staff.permissions.presets.${name}`)}
                </button>
              ))}
              {!preset && (
                <span className={chip(true)}>
                  {t("staff.permissions.custom")}
                </span>
              )}
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-800 mb-1">
              {t("staff.permissions.screens")}
            </p>
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
              {catalog.areas.map(
                ({ area, actions, needs_costs: needsCosts }) => (
                  <li
                    key={area}
                    className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2.5"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900">
                        {t(`staff.permissions.areas.${area}`)}
                      </span>
                      {needsCosts && (
                        <span className="block text-xs text-gray-500">
                          {t("staff.permissions.bringsCosts")}
                        </span>
                      )}
                    </span>
                    <span className="flex flex-wrap gap-x-4 gap-y-1">
                      {actions.map((action) => {
                        const permission = `${area}.${action}`;
                        return (
                          <label
                            key={action}
                            className="flex items-center gap-1.5 text-sm text-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={granted.has(permission)}
                              onChange={() => toggle(permission)}
                              className="h-4 w-4"
                            />
                            {t(`staff.permissions.actions.${action}`)}
                          </label>
                        );
                      })}
                    </span>
                  </li>
                ),
              )}
            </ul>
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-800 mb-1">
              {t("staff.permissions.powersTitle")}
            </p>
            <ul className="space-y-2">
              {catalog.powers.map((power) => {
                const permission = `power.${power}`;
                return (
                  <li key={power}>
                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={granted.has(permission)}
                        onChange={() => toggle(permission)}
                        className="h-4 w-4 mt-0.5"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">
                          {t(`staff.permissions.powers.${power}.label`)}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {t(`staff.permissions.powers.${power}.hint`)}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}

      <p className="text-xs text-gray-500 rounded-lg bg-gray-50 px-3 py-2">
        🔒 {t("staff.permissions.ownerOnlyNote")}
      </p>
    </div>
  );
};

export default PermissionEditor;
